import type { ParentLink, Partnership, Person } from './types';

export interface Graph {
  people: Map<string, Person>;
  parents: Map<string, string[]>;
  children: Map<string, string[]>;
  partners: Map<string, string[]>;
  /** Person ids ordered by creation time (stable tie-break by id). */
  order: string[];
}

export type Blood = { up: number } | { down: number } | { a: number; b: number } | null;

const MAX_DEPTH = 30;

export function buildGraph(
  people: readonly Person[],
  parentLinks: readonly ParentLink[],
  partnerships: readonly Partnership[],
): Graph {
  const pm = new Map<string, Person>();
  for (const p of people) pm.set(p.id, p);
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  const partners = new Map<string, string[]>();
  for (const id of pm.keys()) {
    parents.set(id, []);
    children.set(id, []);
    partners.set(id, []);
  }
  for (const l of parentLinks) {
    if (!pm.has(l.parentId) || !pm.has(l.childId) || l.parentId === l.childId) continue;
    const ps = parents.get(l.childId)!;
    if (ps.includes(l.parentId)) continue;
    ps.push(l.parentId);
    children.get(l.parentId)!.push(l.childId);
  }
  for (const s of partnerships) {
    if (!pm.has(s.aId) || !pm.has(s.bId) || s.aId === s.bId) continue;
    const a = partners.get(s.aId)!;
    if (a.includes(s.bId)) continue;
    a.push(s.bId);
    partners.get(s.bId)!.push(s.aId);
  }
  const order = [...pm.values()]
    .sort((x, y) => x.createdAt - y.createdAt || (x.id < y.id ? -1 : 1))
    .map((p) => p.id);
  return { people: pm, parents, children, partners, order };
}

/** Breadth-first walk. Each reached id maps to its depth and the id it was first reached from. */
function bfsFrom(start: string, next: Map<string, string[]>, cap = MAX_DEPTH): Map<string, { depth: number; from: string }> {
  const out = new Map<string, { depth: number; from: string }>();
  let frontier = [start];
  for (let depth = 1; depth <= cap && frontier.length; depth++) {
    const nf: string[] = [];
    for (const id of frontier) {
      for (const n of next.get(id) ?? []) {
        if (n === start || out.has(n)) continue;
        out.set(n, { depth, from: id });
        nf.push(n);
      }
    }
    frontier = nf;
  }
  return out;
}

function bfs(start: string, next: Map<string, string[]>, cap = MAX_DEPTH): Map<string, number> {
  return new Map([...bfsFrom(start, next, cap)].map(([id, v]) => [id, v.depth]));
}

/** Ancestors keyed by id → depth (1 = parent). */
export function ancestors(g: Graph, id: string): Map<string, number> {
  return bfs(id, g.parents);
}

export function descendants(g: Graph, id: string): Map<string, number> {
  return bfs(id, g.children);
}

export function siblings(g: Graph, id: string): string[] {
  const out = new Set<string>();
  for (const p of g.parents.get(id) ?? []) {
    for (const c of g.children.get(p) ?? []) if (c !== id) out.add(c);
  }
  return [...out];
}

/** Everyone reachable through parent, child or partner edges. */
export function component(g: Graph, id: string): Set<string> {
  const seen = new Set([id]);
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const m of [g.parents, g.children, g.partners]) {
      for (const n of m.get(cur) ?? []) {
        if (!seen.has(n)) {
          seen.add(n);
          stack.push(n);
        }
      }
    }
  }
  return seen;
}

export function connected(g: Graph, a: string, b: string): boolean {
  return component(g, a).has(b);
}

export function bloodBetween(g: Graph, x: string, y: string): Blood {
  return bloodPath(g, x, y)?.blood ?? null;
}

export interface BloodPath {
  blood: Exclude<Blood, null>;
  /** x, then each parent up to the nearest common ancestor (or up to y when y is x's ancestor). */
  mine: string[];
  /** y, then each parent up to the same ancestor (or up to x when x is y's ancestor). */
  theirs: string[];
}

function chain(walk: Map<string, { depth: number; from: string }>, start: string, top: string): string[] {
  const out = [top];
  let cur = top;
  while (cur !== start) {
    cur = walk.get(cur)!.from;
    out.push(cur);
  }
  return out.reverse();
}

/**
 * Like `bloodBetween`, and also the people on each side of the shared ancestor, so callers can tell
 * which parent a relationship runs through (father's or mother's side) and who the linking siblings are.
 */
export function bloodPath(g: Graph, x: string, y: string): BloodPath | null {
  if (x === y) return null;
  const ax = bfsFrom(x, g.parents);
  const up = ax.get(y);
  if (up !== undefined) return { blood: { up: up.depth }, mine: chain(ax, x, y), theirs: [y] };
  const ay = bfsFrom(y, g.parents);
  const down = ay.get(x);
  if (down !== undefined) return { blood: { down: down.depth }, mine: [x], theirs: chain(ay, y, x) };
  let best: { a: number; b: number; id: string } | null = null;
  for (const [id, { depth: a }] of ax) {
    const b = ay.get(id)?.depth;
    if (b === undefined) continue;
    if (!best || a + b < best.a + best.b || (a + b === best.a + best.b && a < best.a)) {
      best = { a, b, id };
    }
  }
  if (!best) return null;
  return { blood: { a: best.a, b: best.b }, mine: chain(ax, x, best.id), theirs: chain(ay, y, best.id) };
}

/** True when adding parent → child would make `child` its own ancestor. */
export function wouldCycle(g: Graph, parentId: string, childId: string): boolean {
  if (parentId === childId) return true;
  return ancestors(g, parentId).has(childId);
}
